import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, X, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

// Dependency-free strict searchable dropdown (no cmdk/radix-select in the UI kit). The visible value is
// ALWAYS one of the fetched options — there is no free-text path: typing only filters, selecting commits.
// Reused ~12× by CreateAddressForm (address referential + fiber cascade). The fetcher is pulled from a ref
// so a new closure each parent render does not retrigger the debounced fetch (which would loop).

export interface ComboOption {
  value: string;
  label: string;
}

interface AddressComboBoxProps {
  /** Selected option value ('' when none). */
  value: string;
  /** Selected option label — shown in the closed field without a refetch. */
  label?: string;
  onChange: (opt: ComboOption | null) => void;
  /** Returns options for the (possibly empty) query. Called debounced while the dropdown is open. */
  fetcher: (q: string) => Promise<ComboOption[]>;
  placeholder?: string;
  disabled?: boolean;
  /** Red border when the field is flagged invalid by the parent. */
  error?: boolean;
  emptyText?: string;
  searchingText?: string;
  clearLabel?: string;
  id?: string;
  /** Allow committing a free-typed value not in the fetched options (shows a "use «query»" item + Enter commits it). */
  allowCustom?: boolean;
  /** Label prefix for the free-text option (e.g. "Use" → «Use "5000"»). */
  customLabel?: string;
}

export function AddressComboBox({
  value,
  label,
  onChange,
  fetcher,
  placeholder,
  disabled,
  error,
  emptyText = 'No results',
  searchingText = 'Searching…',
  clearLabel = 'Clear',
  id,
  allowCustom,
  customLabel = 'Use',
}: AddressComboBoxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ComboOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Close on outside-click (no dropdown primitive — keep it dep-free, like AddressManagementMenu).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Debounced fetch whenever the dropdown opens or the query changes. fetcher is read from the ref so its
  // identity is NOT a dependency (parent re-renders make a fresh closure every time → would loop).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      fetcherRef
        .current(query)
        .then((opts) => {
          if (alive) {
            setOptions(opts);
            setHighlight(0);
          }
        })
        .catch(() => {
          if (alive) setOptions([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [open, query]);

  function choose(opt: ComboOption) {
    onChange(opt);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (options[highlight]) choose(options[highlight]);
      else if (allowCustom && query.trim()) choose({ value: query.trim(), label: query.trim() });
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  }

  // Open + empty query → show the selected label as a placeholder so the current choice stays visible.
  const display = open ? query : label ?? '';
  const placeholderText = open && value ? label || placeholder : placeholder;
  // Free-text ("custom") entry: when allowed and the typed query isn't already an option, offer to use it.
  const trimmed = query.trim();
  const showCustom = !!allowCustom && trimmed !== '' && !options.some((o) => o.label === trimmed);

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          role="combobox"
          aria-expanded={open}
          aria-controls={id ? `${id}-list` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={display}
          placeholder={placeholderText}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          // Close when focus leaves the component (Tab/Shift+Tab) so two lists can't be open at once and a
          // reopen re-runs the fetch. Option/clear use onMouseDown+preventDefault, keeping focus → no blur.
          onBlur={(e) => {
            if (!wrapRef.current?.contains(e.relatedTarget as Node)) {
              setOpen(false);
              setQuery('');
            }
          }}
          className={cn(
            'flex h-10 w-full rounded-lg border bg-white px-4 py-2.5 pr-16 text-sm',
            'placeholder:text-slate-400 focus:outline-none focus:ring-2 transition-colors duration-200',
            'disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-200 focus:border-brand-rose/40 focus:ring-brand-rose/10',
          )}
        />
        <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
          {value && !disabled && (
            <button
              type="button"
              aria-label={clearLabel}
              tabIndex={-1}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(null);
                setQuery('');
              }}
              className="rounded p-0.5 text-slate-400 hover:text-slate-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown className={cn('w-4 h-4 text-slate-400 transition-transform', open && 'rotate-180')} />
        </div>
      </div>

      {open && !disabled && (
        <ul
          id={id ? `${id}-list` : undefined}
          role="listbox"
          className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {loading ? (
            <li className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {searchingText}
            </li>
          ) : (
            <>
              {options.map((opt, i) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(opt);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between px-3 py-2 text-sm',
                    i === highlight ? 'bg-brand-rose/[0.06] text-slate-900' : 'text-slate-700',
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {opt.value === value && <Check className="w-3.5 h-3.5 text-brand-rose shrink-0" />}
                </li>
              ))}
              {options.length === 0 && !showCustom && (
                <li className="px-3 py-2 text-xs text-slate-400">{emptyText}</li>
              )}
              {showCustom && (
                <li
                  role="option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose({ value: trimmed, label: trimmed });
                  }}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-brand-rose hover:bg-brand-rose/[0.06]"
                >
                  <Plus className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{customLabel} « {trimmed} »</span>
                </li>
              )}
            </>
          )}
        </ul>
      )}
    </div>
  );
}
